// Loads oracle JSON datasets and exposes a nearest-neighbour / linear
// interpolator for mock-mode truth lookups. Static imports keep things simple —
// combined oracle payload is ~300 KB which is fine to bundle for Phase 1.

import x57Oracle2d from "../mock-data/oracles/x57_oracle.json";
import x57Oracle3d from "../mock-data/oracles/x57_oracle_3d.json";
import cessna182Oracle from "../mock-data/oracles/cessna182_oracle.json";
import type { OracleDataset } from "../types";

type OracleJsonAxis = {
  name: string;
  units: string;
  min: number;
  max: number;
  uniqueCount?: number;
};

type OracleJsonPoint = {
  inputs: Record<string, number>;
  outputs: Record<string, number>;
};

type OracleJson = {
  id: string;
  label: string;
  axes: OracleJsonAxis[];
  outputs: string[];
  pointCount: number;
  points: OracleJsonPoint[];
};

type LookupSuccess = {
  ok: true;
  outputs: Record<string, number>;
};

type LookupFailure = {
  ok: false;
  reason: "out_of_bounds" | "unknown_dataset" | "unknown_output";
};

export type OracleLookupResult = LookupSuccess | LookupFailure;

// Canonical output mapping per dataset (per API_CONTRACT.md).
// Only canonical keys that resolve to a column with `available: true` are
// returned; the rest are silently dropped so callers see exactly the channels
// the contract advertises.
const DATASET_METADATA: Record<
  string,
  {
    contractId: string;
    label: string;
    axes: OracleDataset["axes"];
    outputMappings: OracleDataset["outputMappings"];
    notes?: string;
  }
> = {
  "x57-bet-2d": {
    contractId: "x57-bet-2d",
    label: "NASA X-57 Maxwell — BET RANS, 2D (alpha × RPM)",
    axes: [
      { name: "alpha", min: -5, max: 20, units: "deg" },
      { name: "RPM", min: 0, max: 6000, units: "rpm" },
    ],
    outputMappings: [
      { canonicalName: "CL", oracleColumnName: "CL", available: true },
      { canonicalName: "CD", oracleColumnName: "CD", available: true },
      { canonicalName: "CY", oracleColumnName: "", available: false },
      { canonicalName: "Cmx", oracleColumnName: "", available: false },
      { canonicalName: "Cmy", oracleColumnName: "", available: false },
      { canonicalName: "Cmz", oracleColumnName: "", available: false },
    ],
    notes:
      "Source: x57_oracle.json. 2D BET RANS lattice; CL and CD only.",
  },
  "x57-bet-3d": {
    contractId: "x57-bet-3d",
    label: "NASA X-57 Maxwell — BET RANS, 3D (alpha × RPM × beta)",
    axes: [
      { name: "alpha", min: -5, max: 20, units: "deg" },
      { name: "RPM", min: 0, max: 6000, units: "rpm" },
      { name: "beta", min: -10, max: 10, units: "deg" },
    ],
    outputMappings: [
      { canonicalName: "CL", oracleColumnName: "CL", available: true },
      { canonicalName: "CD", oracleColumnName: "CD", available: true },
      { canonicalName: "CY", oracleColumnName: "", available: false },
      { canonicalName: "Cmx", oracleColumnName: "CMx", available: true },
      { canonicalName: "Cmy", oracleColumnName: "CMy", available: true },
      { canonicalName: "Cmz", oracleColumnName: "CMz", available: true },
    ],
    notes:
      "Source: x57_oracle_3d.json. 3D BET RANS. CFx/CFy/CFz body-axis forces present in source file but not exposed as canonical outputs in Phase 1.",
  },
  cessna182: {
    contractId: "cessna182",
    label: "Cessna 182 — Flow360 RANS (alpha × beta)",
    axes: [
      { name: "alpha", min: -5, max: 20, units: "deg" },
      { name: "beta", min: -10, max: 10, units: "deg" },
    ],
    outputMappings: [
      { canonicalName: "CL", oracleColumnName: "CL", available: true },
      { canonicalName: "CD", oracleColumnName: "CD", available: true },
      { canonicalName: "CY", oracleColumnName: "CY", available: true },
      { canonicalName: "Cmx", oracleColumnName: "Cl_roll", available: true },
      { canonicalName: "Cmy", oracleColumnName: "Cm_pitch", available: true },
      { canonicalName: "Cmz", oracleColumnName: "Cn_yaw", available: true },
    ],
    notes:
      "Source: cessna182_oracle.json. Flow360 RANS lattice; all six canonical outputs available (moment channels remapped).",
  },
};

// Raw source JSONs keyed by their contract id. The source files use different
// ids ("x57-bet-2d", "x57-bet-3d", "cessna182-rans") — we normalise here.
const RAW_SOURCES: Record<string, OracleJson> = {
  "x57-bet-2d": x57Oracle2d as unknown as OracleJson,
  "x57-bet-3d": x57Oracle3d as unknown as OracleJson,
  cessna182: cessna182Oracle as unknown as OracleJson,
};

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

// Range-check inputs against the contract-declared axis bounds. If a caller
// asks for a point outside the axis window we refuse to extrapolate.
function inBounds(
  datasetId: string,
  inputs: Record<string, number>,
): boolean {
  const meta = DATASET_METADATA[datasetId];
  if (!meta) return false;
  for (const axis of meta.axes) {
    const v = inputs[axis.name];
    if (v == null || !Number.isFinite(v)) return false;
    if (v < axis.min || v > axis.max) return false;
  }
  return true;
}

// Precompute an axis-normalised distance in [0,1]^D for all points in each
// dataset. A tiny kd-tree would be lighter but with <2k points a linear scan
// is fast enough and keeps the mock trivially debuggable.
type PreparedDataset = {
  source: OracleJson;
  axisRanges: Array<{ name: string; min: number; max: number }>;
  points: Array<{
    normInputs: number[];
    rawInputs: Record<string, number>;
    outputs: Record<string, number>;
  }>;
};

const PREPARED: Record<string, PreparedDataset> = {};

function prepare(datasetId: string): PreparedDataset | null {
  if (PREPARED[datasetId]) return PREPARED[datasetId];
  const raw = RAW_SOURCES[datasetId];
  const meta = DATASET_METADATA[datasetId];
  if (!raw || !meta) return null;
  const axisRanges = raw.axes.map((a) => ({
    name: a.name,
    min: a.min,
    max: a.max,
  }));
  const points = raw.points.map((p) => {
    const normInputs = axisRanges.map((a) => {
      const span = a.max - a.min || 1;
      const v = p.inputs[a.name] ?? a.min;
      return (v - a.min) / span;
    });
    return { normInputs, rawInputs: p.inputs, outputs: p.outputs };
  });
  const prepared: PreparedDataset = { source: raw, axisRanges, points };
  PREPARED[datasetId] = prepared;
  return prepared;
}

// Map source-file output names to the canonical names used in the UI.
function remapOutputs(
  datasetId: string,
  raw: Record<string, number>,
): Record<string, number> {
  const meta = DATASET_METADATA[datasetId];
  if (!meta) return {};
  const out: Record<string, number> = {};
  for (const mapping of meta.outputMappings) {
    if (!mapping.available) continue;
    const val = raw[mapping.oracleColumnName];
    if (val != null && Number.isFinite(val)) out[mapping.canonicalName] = val;
  }
  return out;
}

/**
 * Linear-ish interpolation via inverse-distance weighting over the k nearest
 * neighbours in normalised input space. This is deliberately simple and works
 * for both the gridded 2D and the sparser 3D oracles.
 */
export function lookup(
  datasetId: string,
  inputs: Record<string, number>,
): OracleLookupResult {
  const prepared = prepare(datasetId);
  if (!prepared) return { ok: false, reason: "unknown_dataset" };
  if (!inBounds(datasetId, inputs)) {
    return { ok: false, reason: "out_of_bounds" };
  }

  // Build the normalised query vector in the same axis order as prepared points.
  const query = prepared.axisRanges.map((a) => {
    const span = a.max - a.min || 1;
    const raw = inputs[a.name];
    const v = clamp(raw ?? a.min, a.min, a.max);
    return (v - a.min) / span;
  });

  // k nearest neighbours. Linear scan, small datasets.
  const k = Math.min(8, prepared.points.length);
  const heap: Array<{ dist: number; idx: number }> = [];
  for (let i = 0; i < prepared.points.length; i++) {
    const pt = prepared.points[i];
    if (!pt) continue;
    let d2 = 0;
    const pn = pt.normInputs;
    for (let j = 0; j < query.length; j++) {
      const qv = query[j] ?? 0;
      const pv = pn[j] ?? 0;
      const dd = qv - pv;
      d2 += dd * dd;
    }
    heap.push({ dist: Math.sqrt(d2), idx: i });
  }
  heap.sort((a, b) => a.dist - b.dist);
  const neighbours = heap.slice(0, k);

  // Exact hit short-circuit.
  const firstN = neighbours[0];
  const firstPt = firstN ? prepared.points[firstN.idx] : undefined;
  if (firstN && firstPt && firstN.dist < 1e-9) {
    return { ok: true, outputs: remapOutputs(datasetId, firstPt.outputs) };
  }

  // IDW with power 2. Clamp weights to avoid singular behaviour.
  const accum: Record<string, number> = {};
  let wSum = 0;
  for (const { dist, idx } of neighbours) {
    const w = 1 / Math.max(dist * dist, 1e-9);
    wSum += w;
    const pt = prepared.points[idx];
    if (!pt) continue;
    const out = pt.outputs;
    for (const key of Object.keys(out)) {
      const v = out[key];
      if (v == null) continue;
      accum[key] = (accum[key] ?? 0) + w * v;
    }
  }
  const blended: Record<string, number> = {};
  for (const key of Object.keys(accum)) {
    const v = accum[key];
    if (v != null) blended[key] = v / wSum;
  }

  return { ok: true, outputs: remapOutputs(datasetId, blended) };
}

export function listOracleDatasets(): OracleDataset[] {
  const result: OracleDataset[] = [];
  for (const id of Object.keys(DATASET_METADATA)) {
    const meta = DATASET_METADATA[id];
    const raw = RAW_SOURCES[id];
    if (!meta || !raw) continue;
    result.push({
      id: meta.contractId,
      label: meta.label,
      axes: meta.axes,
      outputMappings: meta.outputMappings,
      pointCount: raw.pointCount,
      notes: meta.notes,
    });
  }
  return result;
}

export function getOracleDataset(datasetId: string): OracleDataset | null {
  const meta = DATASET_METADATA[datasetId];
  const raw = RAW_SOURCES[datasetId];
  if (!meta || !raw) return null;
  return {
    id: meta.contractId,
    label: meta.label,
    axes: meta.axes,
    outputMappings: meta.outputMappings,
    pointCount: raw.pointCount,
    notes: meta.notes,
  };
}

// Phase-2 placeholder: we have this dataset pre-staged but don't wire it up yet.
export const LOW_FIDELITY_DATASETS = [
  {
    id: "avl-x57",
    label: "AVL sweep — X-57 (Phase 2)",
    path: "./mock-data/lowfi/avl_x57.csv",
  },
] as const;
